import { useState, useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Area,
  Legend,
} from "recharts";
import { EmptyScreen } from "../../../misc";
import { CustomTooltip } from "@/components/graph/misc";
import { CustomXAxisTick, CustomYAxisTick, roundToTwoDecimalPlaces } from "./utils";
import styles from "../../../styles.module.scss";

interface ExecutionTimeGraphProps {
  data: Array<{
    date: string;
    execution_time: number;
    execution_time_ui: string;
    p90_execution_time: number;
    p90_execution_time_ui: string;
  }>;
}

const ExecutionTimeGraph = ({ data }: ExecutionTimeGraphProps) => {
  const [executionTimeFilter, setExecutionTImeFilter] = useState("all");

  const handleLegendClick = (data: { value?: string }) => {
    const datakey = data?.value;

    switch (datakey) {
      case "Avg Execution Time":
        if (executionTimeFilter === "execution_time") {
          setExecutionTImeFilter("all");
        } else {
          setExecutionTImeFilter("execution_time");
        }
        break;
      case "P90 Execution Time":
        if (executionTimeFilter === "p90_execution_time") {
          setExecutionTImeFilter("all");
        } else {
          setExecutionTImeFilter("p90_execution_time");
        }
        break;
      default:
        setExecutionTImeFilter("all");
    }
  };

  const stackedData = useMemo(() => {
    if (data.length === 0) return [];
    return data.map(entry => ({
      time: entry.date,
      execution_time: roundToTwoDecimalPlaces(entry.execution_time),
      execution_time_ui: entry.execution_time_ui,
      p90_execution_time: roundToTwoDecimalPlaces(entry.p90_execution_time),
      p90_execution_time_ui: entry.p90_execution_time_ui,
      p90_execution_time_stack: roundToTwoDecimalPlaces(
        entry.p90_execution_time - entry.execution_time
      ),
    }));
  }, [data]);

  if (stackedData && stackedData.length === 0)
    return (
      <EmptyScreen
        title="No Execution data found"
        subTitle="No execution data found for the selected period. You will see the data once queries are executed."
      />
    );

  return (
    <ResponsiveContainer minHeight={400} className="mb-4">
      <AreaChart
        data={stackedData}
        margin={{
          top: 50,
          right: 30,
          left: 50,
          bottom: 40,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="time" tick={<CustomXAxisTick />} />
        <YAxis
          label={{
            value: "Query Execution Time",
            fill: "black",
            angle: 0,
            position: "insideTopLeft",
            offset: -30,
          }}
          tick={<CustomYAxisTick />}
        />
        <Tooltip
          content={
            <CustomTooltip
              nameValue={[
                { name: "Date", value: "time", color: "gray" },
                {
                  name: "Avg Execution Time",
                  value: "execution_time_ui",
                  color: "#247EFE",
                },
                {
                  name: "P90 Execution Time",
                  value: "p90_execution_time_ui",
                  color: "#01CD8C",
                },
              ]}
            />
          }
        />
        <Area
          type="linear"
          name="Avg Execution Time"
          dataKey={
            executionTimeFilter === "all" || executionTimeFilter === "execution_time"
              ? "execution_time"
              : "execution_time_hidden"
          }
          stroke="#53B1FD"
          fill="#53B1FD"
          dot={{
            fill: "#C0D7F9",
            strokeWidth: "1px",
            stroke: "#247EFE",
          }}
          stackId={1}
          aria-label="Average execution time"
        />
        <Area
          type="linear"
          name="P90 Execution Time"
          dataKey={
            executionTimeFilter === "all" || executionTimeFilter === "p90_execution_time"
              ? "p90_execution_time"
              : "p90_execution_time_hidden"
          }
          stroke="#52c7a2"
          fill="#52c7a2"
          dot={{
            fill: "#52c7a2",
            strokeWidth: "1px",
            stroke: "#52c7a2",
          }}
          stackId={1}
          aria-label="P90 execution time"
        />
        <Legend
          align="center"
          verticalAlign="bottom"
          wrapperStyle={{ bottom: -20 }}
          onClick={handleLegendClick}
          formatter={(_value: string) => <span className={styles.legend}>{_value}</span>}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default ExecutionTimeGraph;